import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { ArrowLeft, StopCircle, PlayCircle, Trash2, Loader2, Download, FileText, RefreshCw, Clock } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-zinc-100 text-zinc-700 border-zinc-200",
  running: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function humanBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
}

export default function MigrationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const termRef = useRef(null);

  const load = async () => {
    try {
      const [mRes, lRes] = await Promise.all([
        api.get(`/migrations/${id}`),
        api.get(`/migrations/${id}/logs`),
      ]);
      setM(mRes.data);
      setLogs(lRes.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight;
  }, [logs]);

  const action = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/migrations/${id}/${path}`);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const destroy = async () => {
    if (!window.confirm("Delete this migration and its logs?")) return;
    setBusy(true);
    try {
      await api.delete(`/migrations/${id}`);
      toast.success("Deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setBusy(false);
    }
  };

  if (!m) {
    return (
      <div className="p-12 flex items-center gap-2 text-sm font-mono text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const prog = m.progress || {};
  const total = prog.total_messages || 0;
  const done = prog.processed_messages || 0;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <Link to="/dashboard" className="text-sm font-mono text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1" data-testid="details-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
      </Link>

      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-extrabold tracking-tight" data-testid="details-name">{m.name}</h1>
            <span
              data-testid={`details-status-${m.status}`}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-widest ${STATUS_STYLES[m.status]}`}
            >
              {m.status === "running" && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
              {m.status}
            </span>
          </div>
          <p className="font-mono text-xs text-zinc-500">Job ID: {m.id}</p>
        </div>
        <div className="flex items-center gap-2">
          {m.status !== "running" && (m.progress?.processed_messages > 0 || m.status === "completed" || m.status === "failed") && (
            <button
              data-testid="sync-now"
              onClick={() => action("sync-now", "Delta sync started")}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-white border border-zinc-300 text-zinc-900 hover:bg-zinc-50 font-semibold px-4 py-2 rounded-md"
            >
              <RefreshCw className="w-4 h-4" /> Sync now
            </button>
          )}
          <ExportButton mid={m.id} kind="csv" />
          <ExportButton mid={m.id} kind="pdf" />
          {m.status === "running" ? (
            <button
              data-testid="cancel-migration"
              onClick={() => action("cancel", "Cancelling…")}
              disabled={busy}
              className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 hover:bg-zinc-50 font-semibold px-4 py-2 rounded-md"
            >
              <StopCircle className="w-4 h-4" /> Cancel
            </button>
          ) : (
            <button
              data-testid="start-migration"
              onClick={() => action("start", "Started")}
              disabled={busy || m.status === "running"}
              className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#CC3F00] text-white font-semibold px-4 py-2 rounded-md"
            >
              <PlayCircle className="w-4 h-4" /> {m.status === "completed" || m.status === "failed" || m.status === "cancelled" ? "Restart" : "Start"}
            </button>
          )}
          <button
            data-testid="delete-migration"
            onClick={destroy}
            disabled={busy || m.status === "running"}
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-rose-700 font-semibold px-3 py-2 rounded-md"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {m.error && (
        <div className="mt-6 bg-rose-50 border border-rose-200 rounded-md px-5 py-4 text-sm text-rose-800" data-testid="details-error">
          <span className="font-semibold">Error:</span> {m.error}
        </div>
      )}

      {/* Metrics */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Messages" value={`${done.toLocaleString()} / ${total.toLocaleString() || "—"}`} testid="metric-messages" />
        <Metric label="Progress" value={`${pct}%`} testid="metric-progress" />
        <Metric label="Folders" value={`${prog.folders_done || 0} / ${prog.folders_total || 0}`} testid="metric-folders" />
        <Metric label="Transferred" value={humanBytes(prog.bytes_transferred)} testid="metric-bytes" />
      </div>

      {/* Progress bar */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Current folder</span>
          <span className="font-mono text-sm text-zinc-800" data-testid="current-folder">{prog.current_folder || "—"}</span>
        </div>
        <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#FF4F00] transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="progress-bar-fill"
          />
        </div>
      </div>

      {/* Endpoints */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <EndpointCard title="Source" ep={m.source} testid="details-source" />
        <EndpointCard title="Destination" ep={m.destination} testid="details-destination" />
      </div>

      <ScheduleCard migration={m} onUpdate={load} />

      {/* Terminal */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Live log stream</p>
          <p className="text-xs font-mono text-zinc-500">{logs.length} entries</p>
        </div>
        <div ref={termRef} className="terminal h-96" data-testid="log-terminal">
          {logs.length === 0 ? (
            <div className="text-zinc-500">$ waiting for events…</div>
          ) : (
            logs.map((l, i) => (
              <div key={i}>
                <span className="log-time">{new Date(l.ts).toLocaleTimeString()}</span>
                <span className={`log-${l.level}`}>[{l.level.toUpperCase()}] {l.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
      <div className="font-extrabold text-2xl tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function EndpointCard({ title, ep, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid={testid}>
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{title}</p>
      <div className="font-bold text-lg mb-1">{ep.email}</div>
      <div className="font-mono text-xs text-zinc-600">
        {ep.provider} · {ep.host}:{ep.port}
      </div>
    </div>
  );
}

function ExportButton({ mid, kind }) {
  const [busy, setBusy] = useState(false);
  const label = kind === "csv" ? "CSV" : "PDF";
  const Icon = kind === "csv" ? Download : FileText;

  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/migrations/${mid}/export.${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `migration-${mid.slice(0, 8)}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} downloaded`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={download}
      disabled={busy}
      data-testid={`export-${kind}`}
      className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 hover:bg-zinc-50 text-sm font-semibold px-3.5 py-2 rounded-md disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}

function ScheduleCard({ migration, onUpdate }) {
  const s = migration.schedule || { enabled: false, interval_minutes: 60, next_run_at: null };
  const [enabled, setEnabled] = useState(!!s.enabled);
  const [interval, setInterval] = useState(s.interval_minutes || 60);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/migrations/${migration.id}/schedule`, { enabled, interval_minutes: interval });
      toast.success(enabled ? "Schedule enabled" : "Schedule disabled");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-zinc-200 rounded-md p-5" data-testid="schedule-card">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-[#FF4F00]" />
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Automatic delta sync</p>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-[#FF4F00]"
            data-testid="schedule-enabled"
          />
          <span className="font-semibold text-sm">Enabled</span>
        </label>
        <select
          value={interval}
          onChange={(e) => setInterval(parseInt(e.target.value, 10))}
          disabled={!enabled}
          data-testid="schedule-interval"
          className="border border-zinc-300 rounded-md px-3 py-1.5 font-mono text-sm disabled:opacity-50 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
        >
          <option value={15}>Every 15 min</option>
          <option value={60}>Hourly</option>
          <option value={360}>Every 6 hours</option>
          <option value={1440}>Daily</option>
          <option value={10080}>Weekly</option>
        </select>
        <button
          data-testid="schedule-save"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save
        </button>
        {s.next_run_at && enabled && (
          <span className="font-mono text-xs text-zinc-500" data-testid="schedule-next-run">
            Next run: {new Date(s.next_run_at).toLocaleString()}
          </span>
        )}
      </div>
      <p className="mt-3 text-xs font-mono text-zinc-500">
        Delta syncs only transfer new messages (tracked per folder via UIDVALIDITY + UID).
      </p>
    </div>
  );
}
